import { EnrichedEventData, OrderData } from '../../api';

export const SUMMARY_SLOT_SECONDS = 1800;
export const MIN_CARD_WIDTH_PX = 60;
// Days with fewer bookings keep their cards in every view.
export const SUMMARY_MIN_DAY_ITEMS = 8;

export type SummaryItem = {
  id: string;
  source: 'order' | 'event';
  start: number; // seconds from midnight
  end: number; // seconds from midnight
  orderPageId: string;
  persons: number;
};

export type SummaryTypeCount = { orderPageId: string; count: number; persons: number };

export type SummarySlot = {
  start: number; // slot start, seconds from midnight, multiple of SUMMARY_SLOT_SECONDS
  count: number; // orders + events starting in this slot
  persons: number;
  types: SummaryTypeCount[]; // sorted by count desc, then orderPageId asc
  itemIds: string[]; // in start-time order
};

export type SummaryCluster = {
  id: string; // `summary-${start}-${end}`
  start_time: number; // earliest item start, snapped down to the half-hour
  end_time: number; // latest item end, snapped up to the half-hour
  slots: SummarySlot[]; // sorted by start, only slots with at least one item
};

export type DaySummary = {
  collapsedIds: Set<string>;
  clusters: SummaryCluster[];
};

export const EMPTY_DAY_SUMMARY: DaySummary = { collapsedIds: new Set(), clusters: [] };

const sortByStartThenEnd = (items: SummaryItem[]) => [...items].sort((a, b) => a.start - b.start || a.end - b.end);

const groupTypes = (items: SummaryItem[]): SummaryTypeCount[] => {
  const countsByOrderPage = new Map<string, SummaryTypeCount>();
  items.forEach((item) => {
    const typeCount = countsByOrderPage.get(item.orderPageId) ?? {
      orderPageId: item.orderPageId,
      count: 0,
      persons: 0,
    };
    typeCount.count += 1;
    typeCount.persons += item.persons;
    countsByOrderPage.set(item.orderPageId, typeCount);
  });
  return [...countsByOrderPage.values()].sort(
    (a, b) => b.count - a.count || a.orderPageId.localeCompare(b.orderPageId),
  );
};

export const toSummaryItems = (orders: OrderData[], events: EnrichedEventData[]): SummaryItem[] => {
  const orderItems: SummaryItem[] = orders.map((order) => ({
    id: order.id,
    source: 'order',
    start: order.time ?? 0,
    end: (order.time ?? 0) + order.duration,
    orderPageId: order.order_page_id,
    persons: order.persons,
  }));
  const eventItems: SummaryItem[] = events.map((event) => ({
    id: event.id,
    source: 'event',
    start: event.time ?? 0,
    end: (event.time ?? 0) + event.duration,
    orderPageId: event.order_page_id,
    persons: event.used_tickets,
  }));
  return [...orderItems, ...eventItems].filter((item) => item.end > item.start);
};

export const findClusters = (items: SummaryItem[]): SummaryItem[][] => {
  const clusters: SummaryItem[][] = [];
  let runningMaxEnd = 0;
  sortByStartThenEnd(items).forEach((item) => {
    const currentCluster = clusters[clusters.length - 1];
    if (!currentCluster || item.start >= runningMaxEnd) {
      clusters.push([item]);
      runningMaxEnd = item.end;
    } else {
      currentCluster.push(item);
      runningMaxEnd = Math.max(runningMaxEnd, item.end);
    }
  });
  return clusters;
};

export const columnsNeeded = (cluster: SummaryItem[]): number => {
  const points = cluster.flatMap((item) => [
    { time: item.start, delta: 1 },
    { time: item.end, delta: -1 },
  ]);
  // An end at time t is processed before a start at time t.
  points.sort((a, b) => a.time - b.time || a.delta - b.delta);
  let current = 0;
  let maximum = 0;
  points.forEach((point) => {
    current += point.delta;
    maximum = Math.max(maximum, current);
  });
  return maximum;
};

export const buildSlots = (cluster: SummaryItem[]): SummarySlot[] => {
  const itemsBySlotStart = new Map<number, SummaryItem[]>();
  sortByStartThenEnd(cluster).forEach((item) => {
    const slotStart = Math.floor(item.start / SUMMARY_SLOT_SECONDS) * SUMMARY_SLOT_SECONDS;
    itemsBySlotStart.set(slotStart, [...(itemsBySlotStart.get(slotStart) ?? []), item]);
  });
  return [...itemsBySlotStart.entries()]
    .sort(([slotStartA], [slotStartB]) => slotStartA - slotStartB)
    .map(([slotStart, slotItems]) => ({
      start: slotStart,
      count: slotItems.length,
      persons: slotItems.reduce((total, item) => total + item.persons, 0),
      types: groupTypes(slotItems),
      itemIds: slotItems.map((item) => item.id),
    }));
};

export const buildDaySummary = (
  items: SummaryItem[],
  dayColumnWidthPx: number,
  minCardWidthPx = MIN_CARD_WIDTH_PX,
  minDayItems = SUMMARY_MIN_DAY_ITEMS,
): DaySummary => {
  if (dayColumnWidthPx <= 0 || items.length < minDayItems) {
    return EMPTY_DAY_SUMMARY;
  }
  const collapsedIds = new Set<string>();
  const clusters: SummaryCluster[] = [];
  findClusters(items).forEach((cluster) => {
    if (cluster.length < 2 || dayColumnWidthPx / columnsNeeded(cluster) >= minCardWidthPx) {
      return;
    }
    cluster.forEach((item) => collapsedIds.add(item.id));
    // Snapped out to whole half-hours so every slot line sits inside the band.
    const earliest = Math.min(...cluster.map((item) => item.start));
    const latest = Math.max(...cluster.map((item) => item.end));
    const start = Math.floor(earliest / SUMMARY_SLOT_SECONDS) * SUMMARY_SLOT_SECONDS;
    const end = Math.ceil(latest / SUMMARY_SLOT_SECONDS) * SUMMARY_SLOT_SECONDS;
    clusters.push({ id: `summary-${start}-${end}`, start_time: start, end_time: end, slots: buildSlots(cluster) });
  });
  return { collapsedIds, clusters };
};

export const peakSlotCount = (summaries: DaySummary[]): number => {
  const counts = summaries.flatMap((summary) =>
    summary.clusters.flatMap((cluster) => cluster.slots.map((s) => s.count)),
  );
  return counts.length ? Math.max(...counts) : 1;
};

export const buildMonthSummary = (
  items: SummaryItem[],
): { count: number; persons: number; types: SummaryTypeCount[] } => ({
  count: items.length,
  persons: items.reduce((total, item) => total + item.persons, 0),
  types: groupTypes(items),
});
